import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ContentSignatureDetails } from "../components/ContentSignatureDetails";
import { useT } from "../lib/LanguageContext";
import { CATEGORY_ORDER } from "../lib/serviceCategories";

// Signature names stay constant across languages; localised strings come from dictionaries.
const CATEGORIES = {
  "reseaux-sociaux": {
    signatures: [{ name: "FOCUS" }, { name: "GROWTH", withBadge: true }, { name: "SIGNATURE" }, { name: "PREMIUM" }],
  },
  branding: {
    signatures: [{ name: "LOGO" }, { name: "BRANDING ESSENTIEL", withBadge: true }, { name: "BRANDING SIGNATURE" }],
  },
  print: {
    signatures: [{ name: "PRINT ESSENTIEL" }, { name: "PRINT BUSINESS", withBadge: true }, { name: "PRINT SIGNATURE" }],
    extra: "documents",
  },
  contenu: {
    signatures: [{ name: "L'INSTANT" }, { name: "L'EXPRESSION" }, { name: "L'EMPREINTE" }, { name: "SUR MESURE" }],
    rich: true,
  },
  documents: {
    signatures: [{ name: "PRÉSENTATION ESSENTIELLE" }, { name: "PRÉSENTATION BUSINESS", withBadge: true }, { name: "PRÉSENTATION SIGNATURE" }],
  },
};

const slugify = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "-");

const SignatureCard = ({ sig, l, category, badge, rich, contenu }) => (
  <article
    data-testid={`signature-${category}-${slugify(sig.name)}`}
    className="relative h-full bg-[#FFFBEA] rounded-3xl p-8 md:p-10 border border-[#F5E6A3]/60 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 flex flex-col"
  >
    {sig.withBadge && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6B5CE7] text-white text-[10px] uppercase tracking-[0.25em] font-medium px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
        {badge}
      </span>
    )}
    {l.eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-[#6B5CE7] font-medium mb-3">{l.eyebrow}</p>}
    <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1A1528] tracking-wide">{sig.name}</h2>
    {l.subtitle && <p className="font-serif italic text-[#6B5CE7] text-base mt-1 mb-4">{l.subtitle}</p>}
    {rich ? (
      <ContentSignatureDetails l={l} or={contenu.or} />
    ) : (
      <>
        {l.desc && <p className="text-[#4A4558] font-light text-sm md:text-base leading-relaxed mt-2 flex-1">{l.desc}</p>}
        {l.items && (
          <ul className="space-y-2.5 mt-5 flex-1">
            {l.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#1A1528] text-sm font-light">
                <Check size={16} className="text-[#6B5CE7] shrink-0 mt-1" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        )}
        {l.delay && <p className="text-xs uppercase tracking-[0.2em] text-[#6B5CE7] font-medium mt-6">{l.delay}</p>}
        {l.price && <p className="font-serif text-xl text-[#6B5CE7] italic mt-6">{l.price}</p>}
      </>
    )}
  </article>
);

const SignatureGrid = ({ category, rich, contenu }) => {
  const { t } = useT();
  const localised = t(`offresCategories.${category}`);
  return (
    <div className={`max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 ${rich ? "" : "lg:grid-cols-3"} gap-8`}>
      {CATEGORIES[category].signatures.map((sig, i) => (
        <Reveal key={sig.name} delay={i * 0.08}>
          <SignatureCard sig={sig} l={localised.signatures[i] || {}} category={category} badge={localised.badge} rich={rich} contenu={contenu} />
        </Reveal>
      ))}
    </div>
  );
};

const ContenuFooter = ({ c }) => (
  <div className="max-w-2xl mx-auto text-center mt-16">
    <p data-testid="contenu-disclaimer" className="text-xs md:text-sm text-[#4A4558]/80 font-light leading-relaxed">
      {c.disclaimer}
    </p>
    <div className="w-12 h-px bg-[#F5E6A3] mx-auto my-12" />
    <Link to="/contact" data-testid="cta-devis-contenu" className="btn-violet">{c.cta}</Link>
  </div>
);

const OffresCategory = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const entry = CATEGORY_ORDER.find((c) => c.slug === category);

  useEffect(() => {
    if (category === "documents") navigate("/offres/print#documents", { replace: true });
    else if (!entry) navigate("/offres", { replace: true });
  }, [category, entry, navigate]);

  if (!entry) return null;

  const cat = CATEGORIES[category];
  const localised = t(`offresCategories.${category}`);
  const contenu = t("offresCategories.contenu");

  return (
    <main data-testid={`offres-category-${category}`} className="bg-white">
      <section className="pt-36 pb-8 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/offres"
            data-testid="category-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#6B5CE7] hover:text-[#4D3FC8] transition-colors mb-8"
          >
            <ArrowLeft size={14} /> {t("serviceDetail.back")}
          </Link>
          <p className="text-xs tracking-[0.3em] text-[#6B5CE7] font-medium mb-3">{entry.num}</p>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-[#1A1528] uppercase">
            {t(`servicesCatalog.categories.${category}.label`)}
          </h1>
          {cat.rich && (
            <p data-testid="category-intro" className="mt-6 text-lg text-[#4A4558] font-light max-w-3xl">
              {t(`servicesCatalog.categories.${category}.desc`)}
            </p>
          )}
        </div>
      </section>

      <section className="py-20 md:py-28 px-6">
        <SignatureGrid category={category} rich={cat.rich} contenu={contenu} />

        {cat.extra && (
          <div id={cat.extra} data-testid={`section-${cat.extra}`} className="scroll-mt-28 mt-24 md:mt-32">
            <h2 className="max-w-7xl mx-auto font-serif text-3xl md:text-4xl font-bold text-[#1A1528] uppercase mb-12">
              {t("servicesCatalog.documentsTitle")}
            </h2>
            <SignatureGrid category={cat.extra} contenu={contenu} />
          </div>
        )}

        {cat.rich ? (
          <ContenuFooter c={contenu} />
        ) : (
          <div className="max-w-4xl mx-auto text-center mt-20">
            <p className="font-serif italic text-2xl md:text-3xl text-[#6B5CE7] mb-8">{localised.tagline}</p>
            <Link to="/contact" data-testid={`cta-contact-${category}`} className="btn-violet">
              {t("common.devis")}
            </Link>
          </div>
        )}
      </section>
    </main>
  );
};

export default OffresCategory;
